import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getAiRecentConversation } from '@/common/api/api.pleased';
import type { ResumeAIChat } from '@/common/types';
import { END_CHAT_FOR_RESUME_AI_SETUP } from '@/common/utils';
import { clearSelectedTransactionsFromStorage } from '@/common/selectedTransactions.utils';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

/**
 * Composable for handling AI chat resumption
 * Provides methods to fetch recent AI conversations and manage ended chat sessions
 */
export function useResumeAiChat() {
  const enabled = ref(false);

  /**
   * Gets the list of ended session IDs from localStorage
   * @returns Array of session IDs that have been ended
   */
  const getEndedSessionIds = (): string[] => {
    try {
      const stored = localStorage.getItem(END_CHAT_FOR_RESUME_AI_SETUP);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      logger.error('[USE_RESUME_AI_CHAT] Error parsing ended session IDs', { error: parseErrorMessage(e) });
      return [];
    }
  };

  /**
   * Reactive reference to ended session IDs
   * Updates when sessions are marked as ended to trigger computed reactivity
   */
  const endedSessionIds = ref<string[]>(getEndedSessionIds());

  /**
   * Fetches recent AI conversation using TanStack Query
   * Only runs when explicitly enabled
   */
  const {
    data: recentConversation,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ResumeAIChat>({
    queryKey: ['ai-recent-conversation'],
    queryFn: getAiRecentConversation,
    enabled,
    staleTime: 0, // Always fetch fresh data
    retry: 1,
    retryDelay: 1000,
  });

  /**
   * Adds a session ID to the list of ended sessions
   * @param sessionId - The session ID to mark as ended
   */
  const markSessionAsEnded = (sessionId: string | null): void => {
    if (!sessionId) return;

    if (endedSessionIds.value.includes(sessionId)) return;

    endedSessionIds.value = [...endedSessionIds.value, sessionId];
    localStorage.setItem(END_CHAT_FOR_RESUME_AI_SETUP, JSON.stringify(endedSessionIds.value));
  };

  /**
   * Checks if a session ID has been marked as ended
   * @param sessionId - The session ID to check
   * @returns true if the session has been ended
   */
  const isSessionEnded = (sessionId: string | null): boolean => {
    if (!sessionId) return false;
    return endedSessionIds.value.includes(sessionId);
  };

  /**
   * Clears all ended session IDs from localStorage
   */
  const clearEndedSessions = (): void => {
    endedSessionIds.value = [];
    localStorage.removeItem(END_CHAT_FOR_RESUME_AI_SETUP);
  };

  /**
   * Determines if the recent conversation should be resumed
   * Checks if conversation is active and hasn't been ended by the user
   */
  const shouldResumeConversation = computed(() => {
    if (!recentConversation.value) return false;

    const { active, sessionId } = recentConversation.value;
    if (!active || !sessionId) return false;

    return !isSessionEnded(sessionId);
  });

  /**
   * Handles cleanup when conversation is not resumed
   * Clears ended sessions if conversation is not active
   * Clears selected transactions from storage
   */
  const handleNoResume = (): void => {
    if (!recentConversation.value?.active) {
      clearEndedSessions();
    }
    clearSelectedTransactionsFromStorage();
  };

  /**
   * Fetches and checks if there's a conversation to resume
   * Returns the conversation data if it should be resumed, null otherwise
   * @returns Promise resolving to the conversation data or null
   */
  const fetchAndCheck = async (): Promise<ResumeAIChat | null> => {
    // Sync ended session IDs from localStorage so we respect marks from other composable instances (e.g. restart marks session as ended in useAiFlowEventHandlers)
    endedSessionIds.value = getEndedSessionIds();

    // Enable and trigger the query
    enabled.value = true;
    let result = null;
    if (recentConversation.value) {
      result = recentConversation.value;
    } else {
      const refetchResult = await refetch();
      // TanStack refetch does not throw — without this, init skips resume AI as if there was no session.
      if (refetchResult.error) {
        captureEventException(refetchResult.error, ErrorTrackingEvent.ChatbotInitPrefetch);
      }
      result = refetchResult.data;
    }

    // Check if we should resume
    if (result && shouldResumeConversation.value) {
      return result;
    }

    // No resume needed
    handleNoResume();
    return null;
  };

  return {
    // Query state
    recentConversation,
    isLoading,
    isError,
    error,

    // Computed
    shouldResumeConversation,

    // Methods
    getEndedSessionIds,
    markSessionAsEnded,
    isSessionEnded,
    clearEndedSessions,
    handleNoResume,
    fetchAndCheck,
  };
}
